import type { CurvePoint } from './types';
import { getSavedQuotes } from './storage/quotes';
import { getQuoteBook, getResolutionMode, resolveQuoteValue } from './storage/quoteBook';

type CurveLike = {
  points?: CurvePoint[];
};

function resolveCurvePointQuoteId(point: CurvePoint, asOfDate: string): CurvePoint {
  const rawPoint: Record<string, unknown> = { ...(point.point as Record<string, unknown>) };
  const quoteId = typeof rawPoint.quote_id === 'string' ? rawPoint.quote_id : null;
  if (!quoteId) {
    return { ...point, point: rawPoint as any } as CurvePoint;
  }

  const quoteBook = getQuoteBook();
  const resolutionMode = getResolutionMode();
  const bookEntry = quoteBook.find((entry) => entry.id === quoteId);
  let value: number | null = null;

  if (bookEntry) {
    value = resolveQuoteValue(bookEntry.series, asOfDate, bookEntry.resolution_mode || resolutionMode);
  }

  if (value === null) {
    const savedQuote = getSavedQuotes().find((quote) => quote.id === quoteId);
    if (savedQuote && Number.isFinite(savedQuote.value)) {
      value = savedQuote.value;
    }
  }

  if (value === null) {
    throw new Error(`Unknown quote id: ${quoteId}`);
  }

  delete rawPoint.quote_id;
  if (point.point_type === 'BondHelper' && rawPoint.rate === undefined) {
    rawPoint.price = value;
  } else if (point.point_type === 'FutureHelper' && rawPoint.rate === undefined) {
    rawPoint.futures_price = value;
  } else {
    rawPoint.rate = value;
  }

  return {
    ...point,
    point: rawPoint as any,
  } as CurvePoint;
}

export function resolveCurveQuoteIds<T extends CurveLike>(curve: T, asOfDate: string): T {
  return {
    ...curve,
    points: Array.isArray(curve.points)
      ? curve.points.map((point) => resolveCurvePointQuoteId(point, asOfDate))
      : curve.points,
  } as T;
}

export function resolveCurveArrayQuoteIds<T extends CurveLike>(curves: T[], asOfDate: string): T[] {
  return curves.map((curve) => resolveCurveQuoteIds(curve, asOfDate));
}
